namespace Solovis.Reports.DataSetHelpers {


  /**
   * PropValuesToIncludeHelper helps manage retrieval of additional property values for extra columns in data tables.
   */
  export class PropValuesToIncludeHelper {

    private _options;

    private _dataSourceRefForLineItemsDataSet;
    private _sourceLineItemsProperty;
    private _sourceModelEnvironmentProperty;
    private _propValuesDataSourceName;
    private _propValuesToIncludeSetParameterName;
    private _lineItemToResourceIdCreator: (parameters, more) => ((lineItem: any, resourceRef: string) => number | number[]);
    private _lineItemToResourceTypeIdCreator: (parameters, more) => ((lineItem: any, resourceRef: string) => number);
    private _lineItemToAsOfDayIdCreator: (parameters, more) => ((lineItem: any, dateRef: string) => number);

    private _suppressDataSetLoad: (parameters) => boolean;

    /**
     * Data source references to additional model environments to merge. Model environment must be in property called "modelEnvironment".
     */
    private _extraModelEnvironmentDataSetsToMerge;

    /**
     * This will be initialized when the ReportControl sees the setReportControl method on the data source and calls it when it processes
     * the data sources.
     */
    private _reportControl: Solovis.Reports.ReportControl;


    // sourceLineItemsProperty/sourceModelEnvironmentProperty are dotted paths (e.g. "modelEnvironment.managers"),
    // not a single object key, so they need to be walked segment by segment rather than used directly as a
    // bracket-access key.
    private static _getValueAtPath(obj: any, path: string): any {
      return path.split(".").reduce((current, segment) => (current == null ? undefined : current[segment]), obj);
    }

    constructor(options) {
      this._options = options;
      this._dataSourceRefForLineItemsDataSet = options.dataSourceRefForLineItemsDataSet;
      this._sourceLineItemsProperty = options.sourceLineItemsProperty;
      this._sourceModelEnvironmentProperty = options.sourceModelEnvironmentProperty;
      this._propValuesDataSourceName = options.propValuesDataSourceName;
      this._propValuesToIncludeSetParameterName = options.propValuesToIncludeSetParameterName;
      this._lineItemToResourceIdCreator = options.lineItemToResourceIdCreator;
      this._lineItemToAsOfDayIdCreator = options.lineItemToAsOfDayIdCreator;
      this._lineItemToResourceTypeIdCreator = options.lineItemToResourceTypeIdCreator;
      this._suppressDataSetLoad = options.suppressDataSetLoad;

      this._extraModelEnvironmentDataSetsToMerge = options.extraModelEnvironmentDataSetsToMerge;
    }


    private async _dataSetLoadValuesForLineItems(
      more
    ): Promise<any> {
      let parameters = more.parameters;
      let propValuesToInclude = parameters[this._propValuesToIncludeSetParameterName];

      let lineItemsDataSet = more.dataSets[this._dataSourceRefForLineItemsDataSet.name];

      let lineItems;
      if (this._sourceLineItemsProperty) {
        if (lineItemsDataSet) {
          lineItems = PropValuesToIncludeHelper._getValueAtPath(lineItemsDataSet, this._sourceLineItemsProperty);
        }
      }
      else {
        lineItems = lineItemsDataSet;
      }

      let requestedValueSets: SpecificRequestedValueSet[] = [];

      let lineItemToResourceId = this._lineItemToResourceIdCreator(parameters, more);
      let lineItemToAsOfDayId = this._lineItemToAsOfDayIdCreator(parameters, more);

      // pendingIndirectLoads is a list of values that have to be loaded based on other values.
      let pendingIndirectLoads: IndirectRequestedResourcePropertyValue[] = [];

      let handlePropValueToInclude = (
        lineItem,
        propValueToInclude: Solovis.Reports.Parameters.PropValueIncludes.PropValueToInclude,
        indirectLoadStack: IndirectRequestedResourcePropertyValueStackEntry[] = null
      ) => {
        if (propValueToInclude.propDefId) {
          if (propValueToInclude.resourceRef) {
            let asOfDayId = lineItemToAsOfDayId(lineItem, propValueToInclude.dateRef);
            if (Solovis.isNotNull(asOfDayId)) {
              if (propValueToInclude.resourceRef === "indirect") {
                if (propValueToInclude.indirectRef) {
                  // Handle delayed load for indirect properties. After initial load completes, these will
                  //   have to be pulled in on basis of first values that come back.

                  if (!indirectLoadStack) {
                    indirectLoadStack = [];
                  }
                  indirectLoadStack.push({
                    propDefId: propValueToInclude.propDefId,
                    dayId: asOfDayId,
                    exactDayOnly: propValueToInclude.exactDayOnly,
                  });

                  // This will go down the chain to load the original property.
                  handlePropValueToInclude(lineItem, propValueToInclude.indirectRef, indirectLoadStack);
                }
              }
              else {
                let includeResourceId = (newResourceId) => {
                  let valueSet = requestedValueSets.filter(x => x.exactDayOnly === propValueToInclude.exactDayOnly)[0];
                  if (valueSet == null) {
                    valueSet = new SpecificRequestedValueSet();
                    valueSet.exactDayOnly = propValueToInclude.exactDayOnly;
                    requestedValueSets.push(valueSet);
                  }
                  valueSet.AddRequestedValue(newResourceId, propValueToInclude.propDefId, asOfDayId);

                  if (indirectLoadStack && indirectLoadStack.length > 0) {
                    let requestedValue = {
                      resourceId: newResourceId,
                      dayId: asOfDayId,
                      propDefId: propValueToInclude.propDefId,
                      exactDayOnly: propValueToInclude.exactDayOnly
                    };

                    pendingIndirectLoads.push({
                      indirectLoadStack: indirectLoadStack,
                      request: requestedValue
                    });
                  }
                };

                let resourceId = lineItemToResourceId(lineItem, propValueToInclude.resourceRef);
                if (resourceId) {
                  if (_.isArray(resourceId)) {
                    for (let resourceIdEntry of resourceId) {
                      includeResourceId(resourceIdEntry);
                    }
                  }
                  else {
                    includeResourceId(resourceId);
                  }
                }
              }
            }
          }
        }
      };

      if (propValuesToInclude && propValuesToInclude.includeList) {
        $(lineItems).each((i, lineItem) => {
          for (let propValueToIncludeEntry of propValuesToInclude.includeList) {
            let propValueToInclude: Solovis.Reports.Parameters.PropValueIncludes.PropValueToInclude = propValueToIncludeEntry;

            handlePropValueToInclude(lineItem, propValueToInclude);
          }
        });
      }

      let params = {
        requestedValueSets: requestedValueSets,
        includeValueStrings: true,
        includeMetadata: false
      };

      let portalServices = this._reportControl.portalServices();

      let propValueResponse;
      if (!_.isEmpty(requestedValueSets)) {
        if (requestedValueSets.length === 1 && !requestedValueSets[0].exactDayOnly &&
            requestedValueSets[0].dayIds && requestedValueSets[0].dayIds.length === 1) {
          propValueResponse = await portalServices.reportDataLoader().loadDataSetAsync(
            "/Solovis/Metadata/ResourcePropertyValues",
            {
              resourceIdList: requestedValueSets[0].resourceIds,
              propertyDefinitionIdList: requestedValueSets[0].propDefIds,
              asOfDayId: requestedValueSets[0].dayIds[0],
            });
        }
        else {
          propValueResponse = await portalServices.reportDataLoader().loadDataSetAsync(
            "/Solovis/Metadata/SpecificResourcePropertyValues",
            params);
        }
      }
      else {
        propValueResponse = {
          result: {
            dataSet: {}
          }
        };
      }

      if (propValueResponse.error) {
        return propValueResponse;
      }

      let loadedDataSet = propValueResponse.result.dataSet;

      let lineItemToResourceTypeId: ((lineItem: any, resourceRef: string) => number);
      if (this._lineItemToResourceTypeIdCreator) {
        lineItemToResourceTypeId = this._lineItemToResourceTypeIdCreator(more.originalParameters, more);
      }

      let originalModelEnv = null;
      if (this._sourceModelEnvironmentProperty && lineItemsDataSet) {
        originalModelEnv = PropValuesToIncludeHelper._getValueAtPath(lineItemsDataSet, this._sourceModelEnvironmentProperty);
      }

      var newModelEnvironment = new InvData.ModelEnvironment();
      if (originalModelEnv) {
        newModelEnvironment.mergeEnvironmentData(originalModelEnv);
      }

      if (!_.isEmpty(this._extraModelEnvironmentDataSetsToMerge)) {
        for (let extraModelEnvDataSetRef of this._extraModelEnvironmentDataSetsToMerge) {
          var extraModelEnv = more.dataSets[extraModelEnvDataSetRef.name];
          if (extraModelEnv.modelEnvironment) {
            newModelEnvironment.mergeEnvironmentData(extraModelEnv.modelEnvironment);
          }
        }
      }

      var propValueIndex = await SpecificResourcePropertyValuesIndex.createAsync({
        portalServices: portalServices,
        dataSet: loadedDataSet,
        modelEnvironment: newModelEnvironment
      });

      if (pendingIndirectLoads && pendingIndirectLoads.length > 0) {
        await propValueIndex.processIndirectLoads(pendingIndirectLoads);
      }

      await propValueIndex.ensureAllTagSetsAvailable();

      let dataSet = {
        modelEnvironment: newModelEnvironment,
        propValueIndex: propValueIndex,
        lineItemToResourceId: lineItemToResourceId,
        lineItemToAsOfDayId: lineItemToAsOfDayId,
        lineItemToResourceTypeId: lineItemToResourceTypeId
      };

      return dataSet;
    }


    public createDataSource() {
      let self = this;

      let dependsOn = [
        this._dataSourceRefForLineItemsDataSet
      ];

      if (!_.isEmpty(this._extraModelEnvironmentDataSetsToMerge)) {
        dependsOn = dependsOn.concat(this._extraModelEnvironmentDataSetsToMerge);
      }

      let dataSourceForPropertyValues: any = {
        name: this._propValuesDataSourceName,

        dependsOn: dependsOn,

        setReportControl: (reportControl) => {
          self._reportControl = reportControl;
        },

        customDataAsync: async (callback, more) => {
          if (this._suppressDataSetLoad && this._suppressDataSetLoad(more.parameters)) {
            callback({
              dataSet: {
                suppressedLoad: true
              }
            });
          }
          else {
            let dataSet = await this._dataSetLoadValuesForLineItems(more);
            let result = dataSet.error ? dataSet : {
              dataSet: dataSet
            };
            callback(result);
          }
        }
      };

      return dataSourceForPropertyValues;
    }


    public getDataTableControlColumns(options) {
      let tableControl = options.tableControl;
      let parameters = tableControl.parameters;

      let propValuesToInclude: Solovis.Reports.Parameters.PropValueIncludes.PropValuesToIncludeSetOptions = parameters[this._propValuesToIncludeSetParameterName];
      if (!propValuesToInclude || !propValuesToInclude.includeList || propValuesToInclude.includeList.length === 0) {
        return [];
      }

      let propValuesDataSourceName = this._propValuesDataSourceName;
      let propValuesDataSet = tableControl.dataSets[propValuesDataSourceName];
      let lineItemToResourceId = propValuesDataSet.lineItemToResourceId;
      let lineItemToAsOfDayId = propValuesDataSet.lineItemToAsOfDayId;
      let propValueIndex = propValuesDataSet.propValueIndex as SpecificResourcePropertyValuesIndex;

      if (propValuesDataSet.suppressedLoad) {
        return [];
      }

      let getPropValueInstance = (lineItem, propValueToInclude: Solovis.Reports.Parameters.PropValueIncludes.PropValueToInclude) => {
        let dayId = lineItemToAsOfDayId(lineItem, propValueToInclude.dateRef);
        return propValueIndex.getLineItemPropValueInstanceForPropValueToInclude(lineItem, lineItemToResourceId, lineItemToAsOfDayId, propValueToInclude);
      };

      let getMainModelEnv = () => {
        return propValuesDataSet.modelEnvironment as InvData.ModelEnvironment;
      };

      let getExportDataType = function (propDefId) {
        var modelEnv = getMainModelEnv();
        var propDef = modelEnv.propertyDefinitions.tryGetById(propDefId);
        if (propDef) {
          return InvData.Util.GetDataTypeByPropertyType(propDef.propertyTypeId);
        }

        return null;
      };


      let getExportGetter = (propValueToInclude: Solovis.Reports.Parameters.PropValueIncludes.PropValueToInclude) => {
        var modelEnv = getMainModelEnv();
        var propDef = modelEnv.propertyDefinitions.tryGetById(propValueToInclude.propDefId);
        if (propDef) {
          var propTypeIds = InvData.PropertyTypeIds;

          if (propDef.propertyTypeId == propTypeIds.Boolean) {
            return function (row) {
              let propValueInstance = getPropValueInstance(row, propValueToInclude);
              if (propValueInstance) {
                return propValueInstance.value.b;
              }

              return null;
            }
          }
          else if (propDef.propertyTypeId == propTypeIds.Decimal || propDef.propertyTypeId == propTypeIds.Currency) {
            return function (row) {
              let propValueInstance = getPropValueInstance(row, propValueToInclude);
              if (propValueInstance) {
                return propValueInstance.value.d;
              }

              return null;
            }
          }
          else if (propDef.propertyTypeId == propTypeIds.Date) {
            return function (row) {
              let propValueInstance = getPropValueInstance(row, propValueToInclude);
              if (propValueInstance) {
                return propValueInstance.value.dt;
              }

              return null;
            }
          }
          else if (propDef.propertyTypeId == propTypeIds.String
            || propDef.propertyTypeId == propTypeIds.LongText
            || propDef.propertyTypeId == propTypeIds.Html
            || propDef.propertyTypeId == propTypeIds.Url
          ) {
            return function (row) {
              let propValueInstance = getPropValueInstance(row, propValueToInclude);
              if (propValueInstance) {
                return propValueInstance.value.s;
              }

              return null;
            }
          }
          else if (propDef.propertyTypeId == propTypeIds.TagRef) {
            return function (row) {
              let propValueInstance = getPropValueInstance(row, propValueToInclude);
              if (propValueInstance) {
                return propValueInstance.value.t;
              }

              return null;
            }
          }
          else if (propDef.propertyTypeId == propTypeIds.EntityRef) {
            return function (row) {
              let propValueInstance = getPropValueInstance(row, propValueToInclude);
              if (propValueInstance) {
                return propValueInstance.value.e;
              }

              return null;
            };
          }
          else if (propDef.propertyTypeId == propTypeIds.InvestmentRef
            || propDef.propertyTypeId == propTypeIds.ContactRef
            || propDef.propertyTypeId == propTypeIds.NoteRef
            || propDef.propertyTypeId == propTypeIds.TransactionRef
          ) {
            return function (row) {
              let propValueInstance = getPropValueInstance(row, propValueToInclude);
              if (propValueInstance) {
                return propValueInstance.value.r;
              }

              return null;
            };
          }
          else if (propDef.propertyTypeId == propTypeIds.ManagerRef) {
            return function (row) {
              let propValueInstance = getPropValueInstance(row, propValueToInclude);
              if (propValueInstance) {
                return propValueInstance.valueStrings;
              }

              return null;
            };
          }
          else if (propDef.propertyTypeId == propTypeIds.Integer
            || propDef.propertyTypeId == propTypeIds.UserRef
          ) {
            return function (row) {
              let propValueInstance = getPropValueInstance(row, propValueToInclude);
              if (propValueInstance) {
                return propValueInstance.value.i;
              }

              return null;
            };
          }
        }

        return null;
      };

      let columns = [];
      let columnIdsSeen = {};

      var mainModelEnv = getMainModelEnv();

      for (let propValueToIncludeEntry of propValuesToInclude.includeList) {
        let propValueToInclude: Solovis.Reports.Parameters.PropValueIncludes.PropValueToInclude = propValueToIncludeEntry;

        let prop = mainModelEnv.propertyDefinitions.tryGetById(propValueToInclude.propDefId);
        if (!prop) {
          continue;
        }

        let columnId = "prop_";
        if (propValueToInclude.resourceRef) {
          columnId += propValueToInclude.resourceRef;
        }
        columnId += "_";
        if (propValueToInclude.dateRef) {
          columnId += propValueToInclude.dateRef;
        }
        if (propValueToInclude.exactDayOnly) {
          columnId += "_exact";
        }

        columnId += "_";
        columnId += prop.code;

        // Prevent duplicate property columns from being created.
        if (columnIdsSeen[columnId]) {
          if (!propValueToInclude.indirectRef) {
            continue;
          }

          //For indirect reference (property of property), append indirect ref information to column id and check again.
          columnId += propValueToInclude.indirectRef.resourceRef ? "_" + propValueToInclude.indirectRef.resourceRef : "";
          columnId += propValueToInclude.indirectRef.dateRef ? "_" + propValueToInclude.indirectRef.dateRef : "";
          columnId += propValueToInclude.indirectRef.exactDayOnly ? + "_exact" : "";
          let indirectProp = mainModelEnv.propertyDefinitions.tryGetById(propValueToInclude.indirectRef.propDefId);
          columnId += "_" + indirectProp.code;

          if (columnIdsSeen[columnId]) {
            continue;
          }
        }
        columnIdsSeen[columnId] = true;

        let colExportGetter = getExportGetter(propValueToInclude);

        let columnSuperHeaders = [];

        let extraInfo = Solovis.Reports.Parameters.PropValueIncludes.PropValuesToIncludeSetParameterControl.getParamExtraInfo(
          tableControl.reportControl,
          this._propValuesToIncludeSetParameterName,
          propValueToInclude);

        let mainSuperHeader = "";
        if (extraInfo.indirectInfoList) {
          // We need to build super-header off the indirect list.
          for (let indirectCo = 0; indirectCo < extraInfo.indirectInfoList.length; indirectCo++) {
            let indirectInfo = extraInfo.indirectInfoList[indirectCo];
            let propDef = mainModelEnv.propertyDefinitions.tryGetById(indirectInfo.propDefId);
            let propDefLabel = "" + (propDef ? propDef.label : indirectInfo.propDefId);
            if (indirectCo > 0) {
              mainSuperHeader += " For ";
            }
            mainSuperHeader += propDefLabel;
            if (indirectCo === (extraInfo.indirectInfoList.length - 1)) {
              // On the last indirection, we want to refer to the original resource.
              mainSuperHeader += " On " + indirectInfo.resourceRefLabel;
            }
          }
        }
        else {
          // This is a simple direct property reference with no indirection.
          if (extraInfo.resourceRefLabel) {
            mainSuperHeader = extraInfo.resourceRefLabel;
          }
        }
        if (extraInfo.dateRefLabel) {
          mainSuperHeader += " (" + extraInfo.dateRefLabel + (propValueToInclude.exactDayOnly ? (", Exact Day Only") : "") + ")";
        }
        else {
          if (propValueToInclude.exactDayOnly) {
            mainSuperHeader += " (Exact Day Only)";
          }
        }

        if (mainSuperHeader) {
          columnSuperHeaders.push(mainSuperHeader);
        }

        let column: any = {
          id: columnId,
          label: prop.label,
          field: function (row) {
            if (row._groupId === columnId) {
              return row._groupRep;
            }

            if (colExportGetter) {
              var value = colExportGetter(row);
              if (!prop.allowMultiple) {
                if (value && value.length == 1) {
                  return value[0];
                }
              }

              return value;
            }
          },

          superHeaders: columnSuperHeaders,

          exportDataType: getExportDataType(prop.id),
          exportGetter: colExportGetter,

          applyStandardGrouping: true,

          htmlFormatter: (cellValue, cellContext) => {
            if (cellContext.aggregate || cellContext.group) {
              return cellContext.defaultFormatter(cellValue, cellContext);
            }

            let row = cellContext.row;
            let propDefId = prop.id;
            let propTypeId = prop.propertyTypeId;
            let resourceId = lineItemToResourceId(row, propValueToInclude.resourceRef);

            var cellDiv = $("<div>");

            var editLink = null;

            let canEdit = false;

            // NOTE: Multi-resource fallback cells cannot be edited.
            if (!_.isArray(lineItemToResourceId)) {
              if (propValuesDataSet.lineItemToResourceTypeId) {
                let resourceTypeId = propValuesDataSet.lineItemToResourceTypeId(row, propValueToInclude.resourceRef);
                canEdit = this._canEditResourceTypeId(resourceTypeId);
              }
              else {
                canEdit = this._canEditResourceTypeId(extraInfo.resourceTypeId);
              }
            }

            if (canEdit && Solovis.isNotNull(resourceId)) {
              editLink = $('<a><span class="fa fa-pencil"></span>').css({"float": "right", "color": "#444"}).appendTo(cellDiv)
                .addClass("no-print");
            }

            var propDiv = $("<div>").appendTo(cellDiv);

            let propValueInstance = getPropValueInstance(row, propValueToInclude);
            if (propValueInstance) {
              var propHtml = Solovis.Reports.Formatter.formatResourcePropertyValueAsHtml({
                property: propValueInstance.property,
                resourcePropertyValue: propValueInstance,
                printMode: tableControl.printMode,
                forExport: cellContext.forExport ? cellContext.forExport : false
              });

              propDiv.html(propHtml as any);
            }

            if (editLink) {
              editLink.click(function () {
                let propValueInstance = getPropValueInstance(row, propValueToInclude);

                let fromDayId = 0;
                if (propValueInstance && propValueInstance.value) {
                  fromDayId = propValueInstance.value.from;
                }
                else {
                  if (propValueToInclude.exactDayOnly) {
                    fromDayId = lineItemToAsOfDayId(row, propValueToInclude.dateRef);
                  }
                }

                Solovis.Properties.PropertyEditor.editResourceProperty({
                  resourceId: resourceId,
                  propertyDefinitionId: propDefId,
                  propertyTypeId: propTypeId,
                  from: fromDayId,

                  saveCallback: function () {
                    let asOfDayId = lineItemToAsOfDayId(row, propValueToInclude.dateRef);

                    Solovis.Reports.ReportDataLoader.loadDataSetAsync(
                      '/Solovis/Metadata/ResourcePropertyValues',
                      {
                        resourceId: resourceId,
                        propertyDefinitionIdList: [prop.id],
                        asOfDayId: asOfDayId,
                        loadAllValuesThroughTime: false
                      },
                      function (response, success) {
                        if (success) {
                          // We should have 1 property value.
                          var dataSet = response.result.dataSet;

                          // We should only have 1 property definition which is the one we requested.
                          var propDef = null;
                          var modelEnv = dataSet.modelEnvironment;
                          if (modelEnv.propertyDefinitions && modelEnv.propertyDefinitions.length > 0) {
                            propDef = modelEnv.propertyDefinitions[0];
                          }

                          var propValues = dataSet.propertyValues;
                          if (propValues && propValues.length > 0) {
                            var propValueInstance = propValues[0];

                            propValueIndex._augmentResourcePropertyValue(propValueInstance);

                            if (propValueInstance.value.from === asOfDayId || !propValueToInclude.exactDayOnly) {
                              var propHtml = Solovis.Reports.Formatter.formatResourcePropertyValueAsHtml({
                                property: propDef,
                                resourcePropertyValue: propValueInstance,
                                printMode: tableControl.printMode
                              });

                              propDiv.html(propHtml as any);
                            }
                            else {
                              // TODO: Value has been deleted so we need to remove it.
                              propDiv.html("");
                            }

                            propValueIndex._addOrUpdateValue(propValueInstance);
                          }
                          else {
                            propDiv.html("");
                          }
                        }
                      },
                      {
                        forceNoReadOnly: true
                      }
                    );
                  }
                });
              });
            }

            return cellDiv;
          }
        };

        if (!prop.allowMultiple) {
          column.dataType = column.exportDataType;
        }

        var modelEnv = getMainModelEnv();
        var propDef = modelEnv.propertyDefinitions.tryGetById(prop.id);

        if (propDef && propDef.propertyTypeId == InvData.PropertyTypeIds.TagRef) {
          column.tagSetId = propDef.tagRefTagSetId;
        }

        columns.push(column);

        if (propValuesToInclude.showFromDate || propValueToInclude.showFromDate) {
          // Also, create colum for the from date.
          let fromColumn = {
            id: column.id + ".from",
            label: "Since Date For " + column.label,
            superHeaders: column.superHeaders,
            field: function (row) {
              let propValueInstance = getPropValueInstance(row, propValueToInclude);
              if (propValueInstance) {
                return propValueInstance.value.from;
              }

              return null;
            },
            dataType: "dayId",
            htmlFormatter: (cellValue, cellContext) => {
              if (cellValue === 0) {
                return "Always";
              }
              else {
                return cellContext.defaultFormatter(cellValue, cellContext);
              }
            }
          };

          columns.push(fromColumn);
        }
      }

      return columns;
    }

    private _canEditResourceTypeId(resourceTypeId?: number) {
      if (resourceTypeId) {
        switch (resourceTypeId) {
          case InvData.ResourceTypeIds.Entity: return Solovis.Portal.CachedPermissions.doesUserHavePermission("EntityEdit");
          case InvData.ResourceTypeIds.Investment: return Solovis.Portal.CachedPermissions.doesUserHavePermission("InvestmentEdit");
        }
      }

      return false;
    }
  }

}